import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, ChevronDown, User, Eye, FileStack } from "lucide-react";
import { useState } from "react";
import { useAuth, ROLES } from "@/contexts/AuthContext";
import EdilmindLogo from "@/components/EdilmindLogo";

const roleBadgeColor = {
  titolare: "bg-brand-navy text-white",
  direttore_lavori: "bg-blue-700 text-white",
  responsabile_sicurezza: "bg-brand-orange text-white",
  caposquadra: "bg-emerald-600 text-white",
  operaio: "bg-slate-500 text-white",
};

const NavItem = ({ to, active, children, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:text-slate-900"
    }`}
  >
    {children}
  </Link>
);

const ViewAsSelector = () => {
  const { viewAsRole, setViewAsRole } = useAuth();
  const [open, setOpen] = useState(false);
  const active = viewAsRole && ROLES[viewAsRole];

  return (
    <div className="relative" data-testid="view-as-selector">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="view-as-trigger"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-wider transition-colors border ${
          active
            ? `${roleBadgeColor[viewAsRole]} border-transparent`
            : "border-dashed border-slate-300 text-slate-600 hover:border-slate-500 bg-white"
        }`}
        title="Visualizza l'app come la vedrebbe un altro ruolo"
      >
        <Eye size={12} />
        {active ? `Vista: ${ROLES[viewAsRole].label}` : "Visualizza come"}
        <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-label="Chiudi menu"
          />
          <div
            className="absolute right-0 mt-2 w-60 rounded-lg bg-white border border-slate-200 shadow-lg z-40 overflow-hidden"
            data-testid="view-as-menu"
          >
            <button
              type="button"
              data-testid="view-as-reset"
              onClick={() => { setViewAsRole(null); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100"
            >
              Vista normale (Titolare)
            </button>
            {Object.entries(ROLES)
              .filter(([id]) => id !== "titolare")
              .map(([id, info]) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`view-as-${id}`}
                  onClick={() => { setViewAsRole(id); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                >
                  <span className={`w-2 h-2 rounded-full ${roleBadgeColor[id]}`} />
                  {info.label}
                </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Navbar = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, actualRole, effectiveRole, caps, logout } = useAuth();
  const isActive = (p) => pathname === p;

  // Hide the navbar entirely on auth screens (looks cleaner).
  if (["/login", "/pin-login", "/reset-password"].includes(pathname)) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/70"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 gap-4">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="navbar-logo">
          <EdilmindLogo size={34} />
          <span className="font-display font-bold text-base sm:text-lg tracking-tight hidden sm:flex flex-col leading-tight">
            <span className="text-brand-navy">EDILMIND</span>
            <span className="text-[9px] font-semibold tracking-[0.18em] text-brand-orange uppercase -mt-0.5">
              Intelligenza · Ordine · Valore
            </span>
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-2 flex-wrap justify-end">
          {isAuthenticated && (
            <>
              {caps?.canUpload && (
                <NavItem to="/" active={isActive("/")} testId="nav-home">
                  Carica
                </NavItem>
              )}
              <NavItem to="/documenti" active={isActive("/documenti")} testId="nav-history">
                <span className="inline-flex items-center gap-1.5">
                  <FileStack size={15} />
                  Contratti
                </span>
              </NavItem>

              {/* Role simulator for Titolare only */}
              {actualRole === "titolare" && <ViewAsSelector />}

              {/* Role badge */}
              <span
                data-testid="nav-role-badge"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${roleBadgeColor[effectiveRole] || "bg-slate-200 text-slate-800"}`}
                title={user?.name ? `Ciao ${user.name}` : ROLES[effectiveRole]?.label}
              >
                {ROLES[effectiveRole]?.label}
              </span>

              {user?.name && (
                <div className="hidden sm:flex items-center gap-1.5 px-1.5 text-xs text-slate-600" data-testid="nav-user-name">
                  <User size={13} />
                  <span className="font-medium max-w-[100px] truncate">{user.name}</span>
                </div>
              )}

              <button
                type="button"
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                title="Esci"
                className="inline-flex items-center gap-1 p-2 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <LogOut size={15} />
                <span className="hidden sm:inline text-xs font-semibold">Esci</span>
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
